import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Report, type ReportKind, type ReportStatus } from '../api';
import { useAuth } from '../auth';
import {
  Badge,
  Button,
  Chips,
  Field,
  IconPlus,
  Input,
  Modal,
  Page,
  Tabs,
  Textarea,
} from '../ui';

const COLUMNS: { id: ReportStatus; label: string }[] = [
  { id: 'queued', label: 'Queued' },
  { id: 'triaging', label: 'Triaging' },
  { id: 'dispatched', label: 'Dispatched' },
  { id: 'pr_open', label: 'PR open' },
  { id: 'deploying', label: 'Deploying' },
  { id: 'in_review', label: 'In review' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'rejected', label: 'Rejected' },
];

type KindFilter = 'all' | ReportKind;

const KIND_FILTERS: { id: KindFilter; label: string }[] = [
  { id: 'all', label: 'todos' },
  { id: 'bug', label: 'bugs' },
  { id: 'suggestion', label: 'sugestões' },
  { id: 'scenario', label: 'cenários BDD' },
];

export function Board() {
  const { slug } = useParams<{ slug: string }>();
  const { me } = useAuth();
  const nav = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<ReportStatus | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [showNew, setShowNew] = useState(false);

  const canMove = me?.globalRole === 'admin';

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    const load = () =>
      api
        .listReports(slug)
        .then(({ reports }) => alive && setReports(reports))
        .catch(() => alive && setErr('Falha ao carregar reports'));
    load();
    const t = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [slug]);

  const byStatus = useMemo(() => {
    const m = new Map<ReportStatus, Report[]>();
    for (const col of COLUMNS) m.set(col.id, []);
    for (const r of reports) {
      if (kindFilter !== 'all' && r.kind !== kindFilter) continue;
      const arr = m.get(r.status);
      if (arr) arr.push(r);
    }
    return m;
  }, [reports, kindFilter]);

  async function handleDrop(reportId: string, from: ReportStatus, to: ReportStatus) {
    if (!slug || from === to) return;
    setDragOver(null);
    setMoving(reportId);
    setReports((prev) => prev.map((r) => (r.id === reportId ? { ...r, status: to } : r)));
    try {
      await api.updateStatus(slug, reportId, to);
      const { reports: fresh } = await api.listReports(slug);
      setReports(fresh);
    } catch (e) {
      const err = e as { error?: string; status?: number };
      setErr(`Falha ao mover: ${err.error ?? err.status ?? 'erro'}`);
      setReports((prev) => prev.map((r) => (r.id === reportId ? { ...r, status: from } : r)));
    } finally {
      setMoving(null);
    }
  }

  if (err) return <Page title="Board"><div className="ui-error-banner">{err}</div></Page>;
  if (!slug) return null;

  const totalVisible = Array.from(byStatus.values()).reduce((acc, arr) => acc + arr.length, 0);

  return (
    <Page
      title="Board"
      subtitle={`${totalVisible} report${totalVisible === 1 ? '' : 's'} ${kindFilter === 'all' ? 'no total' : `(${KIND_FILTERS.find((f) => f.id === kindFilter)?.label})`}`}
      actions={
        <>
          <Chips items={KIND_FILTERS} value={kindFilter} onChange={setKindFilter} />
          <Button variant="primary" leftIcon={<IconPlus size={14} />} onClick={() => setShowNew(true)}>
            Novo report
          </Button>
        </>
      }
      fluid
    >
      <NewReportModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(reportId) => {
          setShowNew(false);
          if (slug) {
            // Re-fetch the board, then jump to the report detail.
            api.listReports(slug).then(({ reports: fresh }) => setReports(fresh)).catch(() => {});
            nav(`/p/${slug}/r/${reportId}`);
          }
        }}
        slug={slug ?? ''}
      />
      <div className="board">
        {COLUMNS.map((col) => {
          const items = byStatus.get(col.id) ?? [];
          const isOver = dragOver === col.id;
          return (
            <div
              className={`column ${isOver ? 'drag-over' : ''}`}
              key={col.id}
              onDragOver={(e) => {
                if (!canMove) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOver !== col.id) setDragOver(col.id);
              }}
              onDragLeave={(e) => {
                if (e.currentTarget === e.target) setDragOver(null);
              }}
              onDrop={(e) => {
                if (!canMove) return;
                e.preventDefault();
                const reportId = e.dataTransfer.getData('text/report-id');
                const from = e.dataTransfer.getData('text/report-from') as ReportStatus;
                if (reportId && from) void handleDrop(reportId, from, col.id);
              }}
            >
              <h2>
                {col.label}
                <span className="count"> {items.length}</span>
              </h2>
              {items.map((r) => (
                <Link
                  key={r.id}
                  to={`/p/${slug}/r/${r.id}`}
                  className={`card ${moving === r.id ? 'moving' : ''}`}
                  draggable={canMove}
                  onDragStart={(e) => {
                    if (!canMove) return;
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/report-id', r.id);
                    e.dataTransfer.setData('text/report-from', r.status);
                  }}
                >
                  <div className="title">{r.title}</div>
                  <div className="meta">
                    <Badge variant={kindBadgeVariant(r.kind)}>{r.kind}</Badge>
                    <span>{formatWhen(r.createdAt)}</span>
                    {r.ciStatus && <CiBadge status={r.ciStatus} />}
                    {r.autoFixAttempts > 0 && r.status !== 'resolved' && (
                      <Badge variant="suggestion" mono title="tentativas de auto-correção">
                        fix×{r.autoFixAttempts}
                      </Badge>
                    )}
                  </div>
                  {r.triageVerdict && r.triageVerdict !== 'valid' && r.triageNotes && (
                    <div className="card-reason">
                      <strong>{r.triageVerdict}:</strong> {truncate(r.triageNotes, 120)}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          );
        })}
      </div>
    </Page>
  );
}

function kindBadgeVariant(kind: ReportKind): 'bug' | 'suggestion' | 'scenario' {
  if (kind === 'bug') return 'bug';
  if (kind === 'scenario') return 'scenario';
  return 'suggestion';
}

function formatWhen(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

function CiBadge({ status }: { status: 'pending' | 'passed' | 'failed' }) {
  const label = status === 'passed' ? 'CI ✓' : status === 'failed' ? 'CI ✕' : 'CI …';
  const variant = status === 'passed' ? 'ok' : status === 'failed' ? 'bug' : 'neutral';
  return <Badge variant={variant} mono>{label}</Badge>;
}

function NewReportModal({
  open,
  onClose,
  onCreated,
  slug,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (reportId: string) => void;
  slug: string;
}) {
  const [kind, setKind] = useState<'bug' | 'suggestion'>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pageUrl, setPageUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on open so leftover state from a previous attempt doesn't leak in.
  useEffect(() => {
    if (open) {
      setKind('bug');
      setTitle('');
      setDescription('');
      setPageUrl('');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!slug || busy) return;
    setBusy(true);
    setError(null);
    try {
      const url = pageUrl.trim();
      const { reportId } = await api.createReport(slug, {
        kind,
        title: title.trim(),
        description: description.trim(),
        ...(url ? { pageUrl: url } : {}),
      });
      onCreated(reportId);
    } catch (e: unknown) {
      const err = e as { error?: string; status?: number };
      setError(err.error ?? `Falha (${err.status ?? '?'})`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Novo report"
      description="O orchestrator pega na fila e dispara o agente assim que entrar como queued."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="new-report-form"
            variant="primary"
            disabled={busy || !title.trim() || !description.trim()}
          >
            {busy ? 'Criando…' : 'Criar e enfileirar'}
          </Button>
        </>
      }
    >
      <form id="new-report-form" onSubmit={submit} style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <Field label="Tipo">
          <Tabs<'bug' | 'suggestion'>
            items={[
              { id: 'bug', label: 'Bug' },
              { id: 'suggestion', label: 'Sugestão' },
            ]}
            value={kind}
            onChange={setKind}
          />
        </Field>
        <Field label="Título" hint="Resumo curto. O agente usa isso pra nomear branch e PR.">
          <Input
            required
            autoFocus
            placeholder={kind === 'bug' ? 'Login quebra ao colar email com espaço' : 'Adicionar atalho cmd+k pra buscar'}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
          />
        </Field>
        <Field
          label="Descrição"
          hint="Inclua passos pra reproduzir, comportamento atual e esperado. Markdown ok."
        >
          <Textarea
            required
            rows={8}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              kind === 'bug'
                ? 'Passos:\n1. Acessar /login\n2. Colar "user@ex.com " (com espaço)\n3. Clicar Entrar\n\nAtual: 500 sem mensagem.\nEsperado: trim do email, login normal.'
                : 'Hoje precisa abrir cada página pra navegar. Adicionar palette com cmd+k, buscando reports/projetos por título.'
            }
            style={{ fontFamily: 'var(--font-mono)' }}
          />
        </Field>
        <Field label="URL da página (opcional)" hint="Onde o problema foi observado.">
          <Input
            placeholder="https://app.example.com/login"
            value={pageUrl}
            onChange={(e) => setPageUrl(e.target.value)}
            type="url"
          />
        </Field>
        {error && <div className="ui-error-banner">{error}</div>}
      </form>
    </Modal>
  );
}
